import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader,
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader as StyledTableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { FundRequest } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';
import { 
  FaPlus, 
  FaSearch, 
  FaFileExcel, 
  FaFilePdf, 
  FaEye, 
  FaEdit, 
  FaFilter, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUserTie,
  FaMoneyBillWave,
  FaTimes,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLayerGroup,
  FaArrowUp,
  FaArrowDown,
  FaDownload,
  FaClock,
  FaCheck
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const PageContainer = styled.div`
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #141340;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .title-icon {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
    justify-content: flex-start;
  }
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(246, 139, 33, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(246, 139, 33, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ExportButton = styled(OutlineButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    span {
      display: none;
    }
    padding: 0.75rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ color?: string; delay?: number }>`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.delay || 0}s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, ${props => props.color || '#F68B21'} 0%, ${props => props.color || '#e67c1a'} 100%);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    
    .stat-icon {
      animation: ${pulse} 0.6s ease-in-out;
    }
  }
`;

const StatIcon = styled.div<{ bgColor?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, ${props => props.bgColor || '#F68B21'} 0%, ${props => props.bgColor || '#e67c1a'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.3rem;
  box-shadow: 0 8px 24px ${props => props.bgColor || '#F68B21'}40;
  transition: all 0.3s ease;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #141340;
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
`;

const FilterSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  margin-bottom: 2rem;
  animation: ${slideIn} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f8f9fa;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    min-width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  label {
    font-weight: 600;
    color: #141340;
    white-space: nowrap;
    font-size: 0.9rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 2;
  
  @media (max-width: 768px) {
    flex: 1;
  }
  
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 1rem;
  }
  
  input {
    width: 100%;
    padding-left: 40px;
    border-radius: 12px;
    border: 2px solid #e9ecef;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: #F68B21;
      box-shadow: 0 0 0 4px rgba(246, 139, 33, 0.1);
      transform: translateY(-1px);
    }
  }
`;

const FilterButton = styled(OutlineButton)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  font-weight: 600;
  background-color: ${props => props.active ? 'rgba(246, 139, 33, 0.1)' : 'transparent'};
  border-color: ${props => props.active ? '#F68B21' : '#e9ecef'};
  color: ${props => props.active ? '#F68B21' : '#495057'};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? 'rgba(246, 139, 33, 0.2)' : 'rgba(246, 139, 33, 0.05)'};
    border-color: #F68B21;
    color: #F68B21;
    transform: translateY(-1px);
  }
`;

const ClearButton = styled(OutlineButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  border-color: #dc3545;
  color: #dc3545;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #dc3545;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
  }
`;

const TableCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #f1f3f4;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #141340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
`;

const ResultCount = styled.div`
  font-size: 0.95rem;
  color: #495057;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: #F68B21;
    border-radius: 50%;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ApprovalDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.span<{ status: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'Approved': return '#28a745';
      case 'Rejected': return '#dc3545';
      case 'Pending': return '#6c757d';
      case 'INITIATED': return '#007bff';
      case 'PAID': return '#17a2b8';
      default: return '#6c757d';
    }
  }};
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
  }
`;

const AmountText = styled.span`
  font-weight: 700;
  color: #141340;
  font-size: 1rem;
`;

const DateText = styled.span`
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 500;
`;

const RequestId = styled.div`
  font-weight: 700;
  color: #141340;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
`;

const ProjectName = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
  background: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  display: inline-block;
`;

const RequesterInfo = styled.div`
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
  
  .empty-icon {
    font-size: 4rem;
    color: #dee2e6;
    margin-bottom: 1.5rem;
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.75rem;
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const TableActionButton = styled(Button)`
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(246, 139, 33, 0.3);
  }
`;

const ApprovalButton = styled(Button)<{ variant?: 'approve' | 'reject' }>`
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  background: ${props => props.variant === 'reject' ? '#dc3545' : '#28a745'};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.variant === 'reject' ? 'rgba(220, 53, 69, 0.3)' : 'rgba(40, 167, 69, 0.3)'};
  }
`;

const FundRequestsList: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<FundRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<FundRequest[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  const itemsPerPage = 10;

  useEffect(() => {
    const loadFundRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load fund requests based on user role
        let userRequests: FundRequest[] = [];
        
        if (user?.role === 'Project Manager') {
          // For Project Manager, load requests for assigned projects
          const response = await apiService.getFundRequests({ 
            page: 1, 
            limit: 1000 // Get all requests for filtering
          });
          userRequests = response.data;
        } else {
          // For other roles, load all requests
          const response = await apiService.getFundRequests({ 
            page: 1, 
            limit: 1000 // Get all requests for filtering
          });
          userRequests = response.data;
        }
        
        setRequests(userRequests);
        setFilteredRequests(userRequests);
      } catch (error: any) {
        console.error('Error loading fund requests:', error);
        setError('Failed to load fund requests');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadFundRequests();
    }
  }, [user]);

  useEffect(() => {
    let result = [...requests];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        request => 
          (request._id || request.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.requester.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      result = result.filter(request => request.status === statusFilter);
    }
    
    // Apply project filter
    if (projectFilter) {
      result = result.filter(request => request.projectId === projectFilter);
    }
    
    // Default sort by creation date (newest first)
    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    setFilteredRequests(result);
    setCurrentPage(1);
  }, [requests, searchTerm, statusFilter, projectFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Under Review':
      case 'Submitted': return 'warning';
      case 'Paid': 
      case 'PAID': return 'info';
      case 'Partially Approved': return 'primary';
      case 'Cancelled': return 'secondary';
      case 'INITIATED': return 'primary';
      default: return 'secondary';
    }
  };

  const canApproveOrReject = (request: FundRequest): boolean => {
    if (!user) return false;
    
    // Find the current pending approval step
    const pendingStep = request.approvalChain.find(step => step.status === 'Pending');
    if (!pendingStep) return false;
    
    // Check if the current user's role matches the role needed for approval
    const canApprove = pendingStep.role === user.role;
    
    // Special logic for Accountant: can only approve after MD/TD has approved
    if (user.role === 'Accountant') {
      // Check if MD or TD has approved before Accountant can see approval buttons
      const mdTdStep = request.approvalChain.find(step => 
        (step.role === 'MD' || step.role === 'TD') && step.status === 'Approved'
      );
      return canApprove && mdTdStep !== undefined;
    }
    
    return canApprove;
  };

  // Helper to check if Accountant can pay
  const canAccountantPay = (request: FundRequest): boolean => {
    if (!user || user.role !== 'Accountant') return false;
    
    // Check if MD or TD has approved
    const mdTdStep = request.approvalChain.find(step => 
      (step.role === 'MD' || step.role === 'TD') && step.status === 'Approved'
    );
    
    // Only allow pay if MD/TD has approved and request is not already Paid
    return mdTdStep !== undefined && request.status !== 'Paid';
  };

  // Get unique projects for filter
  const uniqueProjects = Array.from(new Set(requests.map(request => request.projectId)))
    .map(projectId => {
      const request = requests.find(r => r.projectId === projectId);
      return {
        id: projectId || '',
        name: request?.projectName || ''
      };
    });

  // Get unique statuses for filter
  const uniqueStatuses = Array.from(new Set(requests.map(request => request.status)));

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDisplayStatus = (step: any) => {
    // If Accountant approved, show "PAID" instead of "Approved"
    if (step.role === 'Accountant' && step.status === 'Approved') {
      return 'PAID';
    }
    return step.status;
  };

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleApprove = async (requestId: string) => {
    try {
      const approvalData = {
        action: 'approve',
        comments: `Approved by ${user?.role}`,
        approvedBy: user?.id,
        approvedAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(requestId, approvalData);
      
      if (response.success) {
        // Update the local state with the approved request
        setRequests(prev => prev.map(req => 
          req._id === requestId || req.id === requestId 
            ? { ...req, ...response.data }
            : req
        ));
        
        showDialog('Success', 'Fund request approved successfully!', 'success');
      } else {
        showDialog('Error', 'Failed to approve fund request: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error approving fund request:', error);
      showDialog('Error', 'Error approving fund request: ' + error.message, 'error');
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      const rejectionData = {
        action: 'reject',
        comments: `Rejected by ${user?.role}`,
        rejectedBy: user?.id,
        rejectedAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(requestId, rejectionData);
      
      if (response.success) {
        // Update the local state with the rejected request
        setRequests(prev => prev.map(req => 
          req._id === requestId || req.id === requestId 
            ? { ...req, ...response.data }
            : req
        ));
        
        showDialog('Success', 'Fund request rejected successfully!', 'success');
      } else {
        showDialog('Error', 'Failed to reject fund request: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error rejecting fund request:', error);
      showDialog('Error', 'Error rejecting fund request: ' + error.message, 'error');
    }
  };

  const handlePayment = async (requestId: string) => {
    try {
      const paymentData = {
        action: 'pay',
        comments: `Payment processed by ${user?.role}`,
        paidBy: user?.id,
        paidAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(requestId, paymentData);
      
      if (response.success) {
        // Update the local state with the paid request
        setRequests(prev => prev.map(req => 
          req._id === requestId || req.id === requestId 
            ? { ...req, ...response.data }
            : req
        ));
        
        showDialog('Success', 'Payment processed successfully!', 'success');
      } else {
        showDialog('Error', 'Failed to process payment: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error processing payment:', error);
      showDialog('Error', 'Error processing payment: ' + error.message, 'error');
    }
  };

  // Calculate statistics
  const totalRequests = requests.length;
  const approvedRequests = requests.filter(r => r.status === 'Approved').length;
  const pendingRequests = requests.filter(r => r.status === 'Under Review').length;
  const rejectedRequests = requests.filter(r => r.status === 'Rejected').length;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <span className="title-icon">
            <IconWrapper icon={FaMoneyBillWave} />
          </span>
          Fund Requests
        </PageTitle>
        <ActionButtons>
          {user?.role !== 'Accountant' && (
            <Link to="/funds/new">
              <ActionButton>
                <IconWrapper icon={FaPlus} />
                New Request
              </ActionButton>
            </Link>
          )}
          <ExportButton>
            <IconWrapper icon={FaFileExcel} />
            <span>Excel</span>
          </ExportButton>
          <ExportButton>
            <IconWrapper icon={FaFilePdf} />
            <span>PDF</span>
          </ExportButton>
        </ActionButtons>
      </PageHeader>

      <StatsContainer>
        <StatCard color="#17a2b8" delay={0.1}>
          <StatIcon bgColor="#17a2b8" className="stat-icon">
            <IconWrapper icon={FaLayerGroup} />
          </StatIcon>
          <StatContent>
            <StatValue>{totalRequests}</StatValue>
            <StatLabel>Total Requests</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#ffc107" delay={0.2}>
          <StatIcon bgColor="#ffc107" className="stat-icon">
            <IconWrapper icon={FaClock} />
          </StatIcon>
          <StatContent>
            <StatValue>{pendingRequests}</StatValue>
            <StatLabel>Pending Review</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#28a745" delay={0.3}>
          <StatIcon bgColor="#28a745" className="stat-icon">
            <IconWrapper icon={FaCheckCircle} />
          </StatIcon>
          <StatContent>
            <StatValue>{approvedRequests}</StatValue>
            <StatLabel>Approved</StatLabel>
          </StatContent>
        </StatCard>
        
        <StatCard color="#dc3545" delay={0.4}>
          <StatIcon bgColor="#dc3545" className="stat-icon">
            <IconWrapper icon={FaExclamationTriangle} />
          </StatIcon>
          <StatContent>
            <StatValue>{rejectedRequests}</StatValue>
            <StatLabel>Rejected</StatLabel>
          </StatContent>
        </StatCard>
      </StatsContainer>

      <FilterSection>
        <FilterHeader>
          <h3>
            <IconWrapper icon={FaFilter} />
            Search & Filter Requests
          </h3>
        </FilterHeader>
        
        <FilterRow>
          <SearchContainer>
            <IconWrapper icon={FaSearch} className="search-icon" />
            <Input 
              type="text" 
              placeholder="Search requests..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
        </FilterRow>

        <FilterRow>
          <FilterGroup>
            <label>Status:</label>
            <Select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <label>Project:</label>
            <Select 
              value={projectFilter} 
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <option value="">All Projects</option>
              {uniqueProjects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </Select>
          </FilterGroup>

          <ClearButton onClick={() => {
            setSearchTerm('');
            setStatusFilter('');
            setProjectFilter('');
          }}>
            <IconWrapper icon={FaTimes} />
            Reset Filters
          </ClearButton>
        </FilterRow>
      </FilterSection>

      <TableCard>
        <CardBody>
          <TableHeader>
            <h3>
              <IconWrapper icon={FaLayerGroup} />
              Fund Requests ({filteredRequests.length})
            </h3>
            <ResultCount>
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredRequests.length)} of {filteredRequests.length} requests
            </ResultCount>
          </TableHeader>
          
          {filteredRequests.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableHeader>Request</StyledTableHeader>
                    <StyledTableHeader>Requester</StyledTableHeader>
                    <StyledTableHeader>Amount</StyledTableHeader>
                    <StyledTableHeader>Status</StyledTableHeader>
                    <StyledTableHeader>Approval Chain</StyledTableHeader>
                    <StyledTableHeader>Date</StyledTableHeader>
                    <StyledTableHeader>Actions</StyledTableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentItems.map((request) => (
                    <TableRow key={request._id || request.id}>
                      <TableCell>
                        <RequestId>
                          <Link to={`/funds/${request._id || request.id}`}>{request._id || request.id}</Link>
                        </RequestId>
                        <ProjectName>{request.projectName}</ProjectName>
                      </TableCell>
                      <TableCell>
                        <RequesterInfo>{request.requester}</RequesterInfo>
                      </TableCell>
                      <TableCell>
                        <AmountText>
                          {formatAmount(request.amount, request.currency)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={getStatusVariant(request.status)}>
                          {request.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <ApprovalDots>
                          {request.approvalChain.map((step, index) => (
                            <Dot key={index} status={getDisplayStatus(step)} title={`${step.role}: ${getDisplayStatus(step)}`} />
                          ))}
                        </ApprovalDots>
                      </TableCell>
                      <TableCell>
                        <DateText>{formatDate(request.createdAt)}</DateText>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <Link to={`/funds/${request._id || request.id}`}>
                            <TableActionButton title="View Details">
                              <IconWrapper icon={FaEye} />
                              View
                            </TableActionButton>
                          </Link>
                          {request.status === 'Draft' && user?.role !== 'Accountant' && (
                            <Link to={`/funds/edit/${request._id || request.id}`}>
                              <TableActionButton title="Edit Request">
                                <IconWrapper icon={FaEdit} />
                                Edit
                              </TableActionButton>
                            </Link>
                          )}
                          {canApproveOrReject(request) && (
                            <>
                              <ApprovalButton 
                                variant="approve" 
                                title="Approve Request"
                                onClick={() => handleApprove(request._id || request.id || '')}
                              >
                                <IconWrapper icon={FaCheck} />
                              </ApprovalButton>
                              <ApprovalButton 
                                variant="reject" 
                                title="Reject Request"
                                onClick={() => handleReject(request._id || request.id || '')}
                              >
                                <IconWrapper icon={FaTimes} />
                              </ApprovalButton>
                            </>
                          )}
                          {canAccountantPay(request) && (
                            <TableActionButton
                              style={{ background: '#17a2b8', color: '#fff' }}
                              title="Pay Request"
                              onClick={() => handlePayment(request._id || request.id || '')}
                            >
                              <IconWrapper icon={FaCheckCircle} />
                              Pay
                            </TableActionButton>
                          )}
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <div className="empty-icon">
                <IconWrapper icon={FaMoneyBillWave} />
              </div>
              <div className="empty-title">No fund requests found</div>
              <div className="empty-description">
                Try adjusting your search or filter criteria, or create a new fund request to get started.
              </div>
            </NoDataMessage>
          )}

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                &laquo;
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <PageButton
                  key={number}
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                >
                  {number}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                &raquo;
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </TableCard>

      <div style={{ 
        textAlign: 'right', 
        padding: '2rem 1rem 1rem', 
        color: '#6c757d', 
        fontSize: '0.8rem' 
      }}>
        Developed by Sandfish
      </div>
      
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </PageContainer>
  );
};

export default FundRequestsList; 